import { ArrowRight, BadgePercent, CalendarDays, HandHeart, HeartHandshake, Home, PawPrint, Scissors, ShoppingBag, ShoppingCart, UserRound } from "lucide-react";
import { Link } from "react-router-dom";
import { LEARN, SITES, siteUrl } from "@/lib/sites";

const ICONS = { shop: ShoppingBag, care: Scissors, community: HeartHandshake };
const TONES = { shop: "bg-marigold/15 text-marigold", care: "bg-teal/10 text-teal", community: "bg-violet-100 text-violet-600" };

/** The bar above the header on phones and tablets (computers have the
 *  tabs in the header): Shop · Care · Community · Learn, big enough to tap. */
export function SiteSwitcher({ current }) {
  return (
    <div className="bg-stoneWarm text-white/85 text-[13px]" data-testid="site-switcher">
      <nav aria-label="PawBazaar sites" className="max-w-7xl mx-auto px-2 sm:px-6 h-11 grid grid-cols-4 sm:flex items-center gap-1 text-center">
        {[...SITES, LEARN].map((s) => (
          s.key === current
            ? <span key={s.key} className="py-2 sm:px-4 rounded-full bg-white/20 text-white font-semibold" aria-current="page">{s.label}</span>
            : <a key={s.key} href={s.url} className="py-2 sm:px-4 rounded-full font-semibold hover:bg-white/10 hover:text-white" data-testid={`switch-${s.key}`}>{s.label}</a>
        ))}
      </nav>
    </div>
  );
}

/** "Everything for your pet": the three sites as cards; the current one is marked. */
export function SitesHero({ current, title = "One account. Everything for your pet." }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="sites-hero">
      <h2 className="font-heading text-2xl sm:text-3xl font-semibold mb-5">{title}</h2>
      <div className="grid sm:grid-cols-3 gap-4">
        {SITES.map((s) => {
          const Icon = ICONS[s.key];
          const here = s.key === current;
          const body = (
            <>
              <span className={`h-11 w-11 rounded-2xl flex items-center justify-center ${TONES[s.key]}`}><Icon className="h-5 w-5" /></span>
              <div className="font-heading text-xl font-semibold mt-4">{s.title}</div>
              <p className="text-sm text-stone-600 mt-1 flex-1">{s.line}</p>
              <span className="mt-4 text-sm font-semibold inline-flex items-center gap-1 text-stoneWarm">
                {here ? "You're here" : <>{s.cta} <ArrowRight className="h-4 w-4" /></>}
              </span>
            </>
          );
          const cls = `rounded-3xl border p-6 flex flex-col transition-shadow ${here ? "border-stoneWarm/30 bg-white" : "border-stone-200 bg-white hover:shadow-md"}`;
          return here
            ? <div key={s.key} className={cls}>{body}</div>
            : <a key={s.key} href={s.url} className={cls} data-testid={`hero-${s.key}`}>{body}</a>;
        })}
      </div>
    </section>
  );
}

const ACTIVE = { shop: "text-amber-700", care: "text-teal", community: "text-violet-700" };

/** Each site's own pages for the bottom menu: {key, label, icon, to, site?, match}.
 *  `match(path, search)` marks the page being shown. */
const PHONE_MENUS = {
  shop: [
    { key: "home", label: "Home", icon: Home, to: "/", match: (p) => p === "/" },
    { key: "shop", label: "Shop", icon: ShoppingBag, to: "/shop", match: (p, q) => /^\/(shop|browse|vendors?|product)/.test(p) && !q.includes("offers=1") },
    { key: "offers", label: "Offers", icon: BadgePercent, to: "/shop?offers=1", match: (p, q) => p === "/shop" && q.includes("offers=1") },
    { key: "cart", label: "Cart", icon: ShoppingCart, to: "/cart", match: (p) => p.startsWith("/cart"), count: true },
    { key: "account", label: "Account", icon: UserRound, to: "/account", match: (p) => p.startsWith("/account") },
  ],
  care: [
    { key: "home", label: "Home", icon: Home, to: "/", match: (p) => p === "/" },
    { key: "services", label: "Services", icon: Scissors, to: "/services", match: (p) => p.startsWith("/service") },
    { key: "bookings", label: "Bookings", icon: CalendarDays, to: "/account", match: (p) => p.startsWith("/account") || p.startsWith("/bookings") },
    { key: "account", label: "Account", icon: UserRound, to: "/account?tab=profile", site: "shop", match: () => false },
  ],
  community: [
    { key: "home", label: "Home", icon: Home, to: "/", match: (p) => p === "/" },
    { key: "adopt", label: "Adopt", icon: PawPrint, to: "/adopt", match: (p) => p.startsWith("/adopt") || p.startsWith("/pets") },
    { key: "drives", label: "Drives", icon: CalendarDays, to: "/drives", match: (p) => p.startsWith("/drives") },
    { key: "donate", label: "Donate", icon: HandHeart, to: "/campaigns", match: (p) => p.startsWith("/campaigns") },
    { key: "mine", label: "Account", icon: UserRound, to: "/account", match: (p) => p.startsWith("/account") },
  ],
};

/** The menu at the bottom of each PawBazaar site on phones and tablets, with
 *  that site's own pages (moving between Shop, Care and Community stays in
 *  the bar at the top). On the shop `cartCount` shows the items in the cart. */
export function PhoneTabBar({ current, cartCount = 0, path = "/", search = "" }) {
  const tabs = PHONE_MENUS[current];
  return (
    <>
      <div className="lg:hidden h-16" aria-hidden />
      <nav aria-label={`${SITES.find((x) => x.key === current)?.label} menu`} data-testid="phone-tab-bar"
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-stone-200 bg-white/95 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = t.match(path, search);
            const count = t.count ? cartCount : 0;
            const cls = `relative flex flex-col items-center justify-center gap-0.5 h-16 text-[11px] ${active ? `${ACTIVE[current]} font-bold` : "text-stone-600 font-semibold"}`;
            const body = (
              <>
                <Icon className="h-[22px] w-[22px]" strokeWidth={active ? 2.4 : 2} aria-hidden />
                {count > 0 && <span className="absolute top-1.5 left-[calc(50%+4px)] min-w-[18px] h-[18px] px-1 rounded-full bg-stoneWarm text-white text-[10px] font-bold flex items-center justify-center" data-testid="phone-tab-cart-count">{count > 99 ? "99+" : count}</span>}
                <span className="truncate max-w-full px-1">{t.label}</span>
              </>
            );
            const label = count > 0 ? `${t.label}, ${count} items` : undefined;
            return t.site && t.site !== current
              ? <a key={t.key} href={`${siteUrl(t.site)}${t.to}`} className={cls} aria-label={label} data-testid={`phone-tab-${t.key}`}>{body}</a>
              : <Link key={t.key} to={t.to} className={cls} aria-current={active ? "page" : undefined} aria-label={label} data-testid={`phone-tab-${t.key}`}>{body}</Link>;
          })}
        </div>
      </nav>
    </>
  );
}
